import {
  Grid,
  Box,
  IconButton,
  Button,
  Typography,
  TextField,
  Chip,
  Autocomplete,
  Skeleton,
  useMediaQuery,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LinkIcon from "@mui/icons-material/Link";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import DescriptionIcon from "@mui/icons-material/Description";

import { useEffect, useState, useCallback } from "react";
import DatagridCustom from "../General/DatagridCustom";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "@mui/material/styles";

import {
  getUnlinkedNotificationCertificates,
  downloadUnlinkedNotificationCertificate,
  linkUnlinkedNotificationCertificate,
  discardUnlinkedNotificationCertificate,
  searchLegalFilesForUnlinkedNotificationCertificate,
  updateUnlinkedNotificationCertificate,
} from "../../services/UnlinkedNotificationCertificateService";

import AppDialog from "../General/Dialog/AppDialog";
import ConfirmDialog from "../General/Dialog/ConfirmDialog";
import EditDialog from "../General/Dialog/EditDialog";
import { useToast } from "../../context/ToastContext";

export default function ManagerUnlinkedNotificationCertificate() {
  const { showToast } = useToast();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterValues: [],
  });

  const [openPreview, setOpenPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const [openLink, setOpenLink] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [legalFileOptions, setLegalFileOptions] = useState([]);
  const [selectedLegalFile, setSelectedLegalFile] = useState(null);
  const [legalFileSearch, setLegalFileSearch] = useState("");
  const [linkLoading, setLinkLoading] = useState(false);

  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState({
    notification_date: "",
    status: "",
    label_color: "",
    label_code: "",
    bottom_barcode_code: "",
    extracted_legal_file_code: "",
    reason: "",
  });

  const [openDiscardConfirm, setOpenDiscardConfirm] = useState(false);
  const [discardRow, setDiscardRow] = useState(null);
  const [discardLoading, setDiscardLoading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [previewLoading, setPreviewLoading] = useState(false);

  const { hasPermission } = useAuth();

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const search = filterModel.quickFilterValues?.join(" ") || "";

      const response = await getUnlinkedNotificationCertificates({
        page: paginationModel.page + 1,
        perPage: paginationModel.pageSize,
        search,
        review_status: "pending",
      });

      setRows(response.data.data || []);
      setTotal(response.data.total || 0);
    } catch {
      showToast("No se pudieron cargar las cédulas no vinculadas.", "error")
    } finally {
      setLoading(false);
    }
  }, [paginationModel, filterModel, showToast]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    const run = async () => {
      if (!legalFileSearch?.trim()) {
        setLegalFileOptions([]);
        return;
      }

      try {
        const response =
          await searchLegalFilesForUnlinkedNotificationCertificate(
            legalFileSearch,
          );
        setLegalFileOptions(response.data.data || []);
      } catch {
        setLegalFileOptions([]);
      }
    };

    const timeout = setTimeout(run, 300);
    return () => clearTimeout(timeout);
  }, [legalFileSearch]);

  const handlePreview = async (id) => {
    try {
      setPreviewLoading(true);
      const response = await downloadUnlinkedNotificationCertificate(id);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setOpenPreview(true);
    } catch {
      showToast("No se pudo cargar el PDF.", "error")
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setOpenPreview(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const handleOpenLink = (row) => {
    setSelectedRow(row);
    setSelectedLegalFile(null);
    setLegalFileSearch(row?.extracted_legal_file_code || "");
    setOpenLink(true);
  };

  const handleCloseLink = () => {
    setOpenLink(false);
    setSelectedRow(null);
    setSelectedLegalFile(null);
    setLegalFileSearch("");
    setLegalFileOptions([]);
  };

  const handleConfirmLink = async () => {
    if (!selectedRow || !selectedLegalFile) {
      showToast("Debe seleccionar un expediente.", "error")
      return;
    }

    try {
      setLinkLoading(true);

      const res = await linkUnlinkedNotificationCertificate(selectedRow.id, {
        legal_file_id: selectedLegalFile.id,
      });

      if (res.data?.status === "success") {
        showToast(res.data.message, "success")
        handleCloseLink();
        await fetchRows();
      }
    } catch (error) {
      showToast(
        error?.response?.data?.message || "No se pudo vincular la cédula.",
      "error")
    } finally {
      setLinkLoading(false);
    }
  };

  const handleOpenDiscardConfirm = (row) => {
    setDiscardRow(row);
    setOpenDiscardConfirm(true);
  };

  const handleDiscard = async () => {
    if (!discardRow) return;

    try {
      setDiscardLoading(true);

      const res = await discardUnlinkedNotificationCertificate(discardRow.id);

      if (res.data?.status === "success") {
        showToast(res.data.message, "success")
        setOpenDiscardConfirm(false);
        setDiscardRow(null);
        await fetchRows();
      }
    } catch (error) {
      showToast(
        error?.response?.data?.message || "No se pudo descartar la cédula.",
      "error")
    } finally {
      setDiscardLoading(false);
    }
  };

  const handleEditOpen = (row) => {
    setSelectedRow(row);
    setEditData({
      notification_date: row.notification_date
        ? String(row.notification_date).substring(0, 10)
        : "",
      status: row.status || "",
      label_color: row.label_color || "",
      label_code: row.label_code || "",
      bottom_barcode_code: row.bottom_barcode_code || "",
      extracted_legal_file_code: row.extracted_legal_file_code || "",
      reason: row.reason || "",
    });
    setOpenEdit(true);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedRow(null);
  };

  const handleEditSave = async () => {
    if (!selectedRow) return;

    try {
      const payload = {
        notification_date: editData.notification_date || null,
        status: editData.status || null,
        label_color: editData.label_color || null,
        label_code: editData.label_code || null,
        bottom_barcode_code: editData.bottom_barcode_code || null,
        extracted_legal_file_code: editData.extracted_legal_file_code || null,
        reason: editData.reason || null,
      };

      const res = await updateUnlinkedNotificationCertificate(
        selectedRow.id,
        payload,
      );

      if (res.data?.status === "success") {
        showToast(res.data.message, "success")
        handleCloseEdit();
        await fetchRows();
      }
    } catch (error) {
      showToast(
        error?.response?.data?.message ||
          "No se pudo actualizar la cédula no vinculada.",
      "error")
    }
  };

  const columns = [
    {
      field: "original_name",
      headerName: "Archivo",
      flex: 2,
    },
    {
      field: "stage_text",
      headerName: "Etapa",
      flex: 1,
    },
    // {
    //   field: "result_status_text",
    //   headerName: "Resultado",
    //   flex: 1,
    // },
    // {
    //   field: "status_text",
    //   headerName: "Estado",
    //   flex: 1,
    // },
    // {
    //   field: "extracted_legal_file_code",
    //   headerName: "Expediente extraído",
    //   flex: 1,
    //   valueGetter: (params) => params || "—",
    // },
    {
      field: "label_code",
      headerName: "Código etiqueta",
      flex: 1,
      valueGetter: (params) => params || "—",
    },
    {
      field: "bottom_barcode_code",
      headerName: "Barcode inferior",
      flex: 1,
      valueGetter: (params) => params || "—",
    },
    {
      field: "label_color_text",
      headerName: "Color Etiqueta",
      flex: 1,
      renderCell: (params) => {
        const chipProps = {
          label: params.value || "SIN COLOR",
          variant: "outlined",
        };

        if (params.row.label_color === "yellow") {
          chipProps.sx = {
            color: "#9a6700",
            borderColor: "#eab308",
            backgroundColor: "#fef9c3",
            fontWeight: 700,
          };
        } else if (params.row.label_color === "orange") {
          chipProps.sx = {
            color: "#c2410c",
            borderColor: "#fb923c",
            backgroundColor: "#ffedd5",
            fontWeight: 700,
          };
        }

        return <Chip {...chipProps} />;
      },
    },
    // {
    //   field: "notification_date",
    //   headerName: "Fecha",
    //   flex: 1,
    //   valueGetter: (params) => params || "—",
    // },
    {
      field: "reason",
      headerName: "Motivo",
      flex: 2,
      valueGetter: (params) => params || "—",
    },
    {
      field: "actions",
      headerName: "Acciones",
      flex: 3,
      sortable: false,
      renderCell: (params) => (
        <>
          <IconButton
            color="primary"
            onClick={() => handlePreview(params.row.id)}
            disabled={!hasPermission("ver cedulas notificacion sin vincular")}
          >
            <VisibilityIcon />
          </IconButton>

          <IconButton
            color="warning"
            onClick={() => handleEditOpen(params.row)}
            disabled={
              !hasPermission("actualizar cedula notificacion sin vincular")
            }
          >
            <EditIcon />
          </IconButton>

          <IconButton
            color="success"
            onClick={() => handleOpenLink(params.row)}
            disabled={
              !hasPermission("vincular cedula notificacion sin vincular")
            }
          >
            <LinkIcon />
          </IconButton>

          <IconButton
            color="error"
            onClick={() => handleOpenDiscardConfirm(params.row)}
            disabled={
              !hasPermission("descartar cedula notificacion sin vincular")
            }
          >
            <DeleteSweepIcon />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12 }}>

        <DatagridCustom
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          title="Cédulas no vinculadas"
          description="Listado de cédulas cargadas que no pudieron vincularse automáticamente a un expediente."
          icon={<DescriptionIcon sx={{ fontSize: 32 }} />}
          paginationModel={paginationModel}
          onPaginationChange={setPaginationModel}
          filterModel={filterModel}
          onFilterChange={setFilterModel}
          filterMode="server"
          total={total}
          loading={loading}
          isServerPagination={true}
        />
      </Grid>

      <AppDialog
        open={openLink}
        onClose={handleCloseLink}
        title="Vincular cédula manualmente"
        maxWidth="md"
        actions={
          <>
            <Button onClick={handleCloseLink}>Cancelar</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirmLink}
              disabled={linkLoading || !selectedLegalFile}
            >
              {linkLoading ? "Vinculando..." : "Vincular"}
            </Button>
          </>
        }
      >
        {selectedRow && (
          <Box sx={{ display: "grid", gap: 2 }}>
            <TextField
              label="Archivo"
              value={selectedRow.original_name || ""}
              fullWidth
              InputProps={{ readOnly: true }}
            />

            <TextField
              label="Código extraído"
              value={selectedRow.extracted_legal_file_code || ""}
              fullWidth
              InputProps={{ readOnly: true }}
            />

            <TextField
              label="Motivo"
              value={selectedRow.reason || ""}
              fullWidth
              multiline
              minRows={2}
              InputProps={{ readOnly: true }}
            />

            <Autocomplete
              options={legalFileOptions}
              value={selectedLegalFile}
              onChange={(_, value) => setSelectedLegalFile(value)}
              getOptionLabel={(option) =>
                `${option.code} - ${option.name || ""}`
              }
              onInputChange={(_, value) => setLegalFileSearch(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Buscar expediente"
                  placeholder="Buscar por código o nombre"
                  fullWidth
                />
              )}
            />
          </Box>
        )}
      </AppDialog>

      <AppDialog
        open={openPreview}
        onClose={handleClosePreview}
        title="Vista previa del archivo"
        maxWidth="lg"
        fullScreenMobile
        fullScreenBreakpoint="md"
        stickyHeader
        contentSx={{ p: 0 }}
        paperSx={{ borderRadius: isMobile ? 0 : 4, overflow: "hidden" }}
        actions={
          !isMobile ? (
            <Button
              onClick={handleClosePreview}
              sx={{ borderRadius: 2, fontWeight: 800 }}
            >
              Cerrar
            </Button>
          ) : null
        }
      >
        {previewLoading ? (
          <Box sx={{ p: 2 }}>
            <Skeleton variant="rounded" height={isMobile ? 420 : 680} />
          </Box>
        ) : previewUrl ? (
          <iframe
            src={previewUrl}
            style={{
              width: "100%",
              height: isMobile ? "calc(100vh - 56px)" : "82vh",
              border: 0,
            }}
            title="preview"
          />
        ) : (
          <Box sx={{ p: 2 }}>
            <Typography>No se pudo cargar el archivo.</Typography>
          </Box>
        )}
      </AppDialog>

      <EditDialog
        open={openEdit}
        onClose={handleCloseEdit}
        onSave={handleEditSave}
        title={`Detalle de Cédula No Vinculada ${selectedRow ? `#${selectedRow.id}` : ""}`}
        subtitle="Revise la información general y edite únicamente los campos habilitados."
        values={{
          ...selectedRow,
          ...editData,
          original_name:
            selectedRow?.original_name || selectedRow?.file_name || "",
          stage_text: selectedRow?.stage_text || selectedRow?.stage || "",
          storage_path: selectedRow?.storage_path || "",
          created_at: selectedRow?.created_at
            ? new Date(selectedRow.created_at).toLocaleString("es-AR")
            : "",
        }}
        setValues={setEditData}
        saveDisabled={
          !hasPermission("actualizar cedula notificacion sin vincular")
        }
        sections={[
          {
            title: "Información general",
            description: "Datos solo de lectura.",
            icon: <DescriptionIcon />,
            fields: [
              {
                name: "original_name",
                label: "Archivo original",
                readOnly: true,
              },
              {
                name: "stage_text",
                label: "Etapa",
                readOnly: true,
              },
              {
                name: "storage_path",
                label: "Ruta en storage",
                readOnly: true,
              },
              {
                name: "created_at",
                label: "Creado",
                readOnly: true,
              },
            ],
          },
          {
            title: "Datos editables",
            description: "Campos que pueden modificarse.",
            icon: <EditIcon />,
            fields: [
              {
                name: "notification_date",
                label: "Fecha de notificación",
                type: "date",
              },
              {
                name: "status",
                label: "Estado",
                type: "select",
                options: [
                  { value: "success", label: "Logrado" },
                  { value: "failed", label: "No logrado" },
                  { value: "processed", label: "Procesado" },
                ],
              },
              {
                name: "label_color",
                label: "Color etiqueta",
                type: "select",
                options: [
                  { value: "yellow", label: "Amarillo" },
                  { value: "orange", label: "Naranja" },
                ],
              },
              {
                name: "extracted_legal_file_code",
                label: "Código expediente extraído",
              },
              {
                name: "label_code",
                label: "Código etiqueta",
              },
              {
                name: "bottom_barcode_code",
                label: "Código barra inferior",
              },
              {
                name: "reason",
                label: "Motivo",
                multiline: true,
                minRows: 3,
                md: 12,
              },
            ],
          },
        ]}
      />

      <ConfirmDialog
        open={openDiscardConfirm}
        onClose={() => {
          setOpenDiscardConfirm(false);
          setDiscardRow(null);
        }}
        onConfirm={handleDiscard}
        title="Confirmar descarte"
        description={`¿Seguro que deseas descartar la cédula ${discardRow?.original_name || ""}?`}
        confirmText={discardLoading ? "Descartando..." : "Descartar"}
        cancelText="Cancelar"
        confirmColor="error"
        loading={discardLoading}
      />
    </Grid>
  );
}
